import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, MediaPreviewDialog } from '@xxtract/ui';

import { DOCUMENT_DISPLAY_TYPES } from './MediaCard';

import type { Sortering } from '@xxtract/tabelfilters';
import { SorteerbareKop, ariaSort } from '@/components/tabel';
import { formatDate } from '@/lib/formatters';
import type { MediaLibraryItem } from '../media.api';
import { MEDIA_LIJST } from '../mediaLijst';

/** Scheidingsteken tussen gegevens — geen tekst voor de gebruiker. */
const SCHEIDING = '\u00b7';

/** Pictogramnamen en opmaakwaarden — geen tekst voor de gebruiker. */
const IMAGE_ICON = 'image';
const SPINNER_ICON = 'progress_activity';
const DOWNLOAD_ICON = 'download';
const LEEG = '\u2014';


export interface MediaListProps {
  items: MediaLibraryItem[];
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (id: string) => void;
  onDownload: (item: MediaLibraryItem) => void;
  downloadingIds?: ReadonlySet<string>;
  /** Haalt het adres van de grote versie op — pas wanneer het venster opengaat. */
  loadWebImage?: (item: MediaLibraryItem) => Promise<string | null>;
  productHref?: (item: MediaLibraryItem) => string | undefined;
  /** Met `onSorteer` sorteren de koppen bestand, type, product en datum met een klik (spec-tabelfilters CAP-5). */
  sortering?: Sortering;
  onSorteer?: (sleutel: string) => void;
}

const STANDAARD = MEDIA_LIJST.standaardSortering?.({}) ?? [];
const KOP_CLASS = 'p-2 text-xs font-semibold text-[var(--foreground-muted)]';

/**
 * De bibliotheek als lijst in plaats van tegels: één regel per bestand, met een kleine miniatuur
 * en de gegevens in kolommen ernaast. Bedoeld om veel bestanden naast elkaar te vergelijken —
 * tegels lezen prettiger, een lijst vergelijkt beter.
 *
 * De kolommen afmetingen, formaat en grootte staan hier bewust nog niet volledig: de mediaserver
 * levert breedte, hoogte en bestandssoort niet, en de kolom voor de grootte is in de bron bij elk
 * bestand leeg. Wat er wél is wordt getoond; de rest komt erbij zodra die gegevens bestaan.
 */
export function MediaList({ items, selectedIds, onToggleSelect, onDownload, downloadingIds, loadWebImage, productHref, sortering = [], onSorteer }: MediaListProps) {
  const { t } = useTranslation();

  return (
    <div className="relative overflow-x-auto rounded-[10px] border border-[var(--border)]" data-testid="media-list">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[var(--border)] bg-[var(--background-muted)] text-left">
            <th className="w-10 p-2" scope="col"><span className="sr-only">{t('media.library.list.selectColumn')}</span></th>
            <th className="w-14 p-2" scope="col"><span className="sr-only">{t('media.library.list.previewColumn')}</span></th>
            {MEDIA_LIJST.kolommen.map((kolom) => (
              <th
                aria-label={onSorteer ? t(kolom.kopSleutel) : undefined}
                aria-sort={onSorteer ? ariaSort(sortering, kolom.sleutel) : undefined}
                className={KOP_CLASS}
                key={kolom.sleutel}
                scope="col"
              >
                {onSorteer ? (
                  <SorteerbareKop kolom={kolom} onKlik={() => onSorteer(kolom.sleutel)} sortering={sortering} standaard={STANDAARD} />
                ) : t(kolom.kopSleutel)}
              </th>
            ))}
            <th className="w-28 p-2" scope="col"><span className="sr-only">{t('media.library.list.actionsColumn')}</span></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const selected = selectedIds.has(item.id);
            const busy = downloadingIds?.has(item.id) ?? false;
            const href = productHref?.(item);
            const datum = item.uploadDate == null ? null : new Date(item.uploadDate);
            const regelLabel = item.productId == null
              ? t('media.library.card.unlinked')
              : t('media.library.card.productLine', { brand: item.brand, productName: item.productName, gtin: item.gtin });
            return (
              <tr className="border-b border-[var(--border)] last:border-0" data-testid={`media-row-${item.id}`} key={item.id}>
                <td className="p-2">
                  <input
                    aria-label={t(selected ? 'media.library.card.deselect' : 'media.library.card.select', { fileName: item.fileName })}
                    checked={selected}
                    className="size-4"
                    onChange={() => onToggleSelect(item.id)}
                    type="checkbox"
                  />
                </td>
                <td className="p-2">
                  {/* Documenten hebben geen webversie; die blijven een stil pictogram. */}
                  {loadWebImage != null && !DOCUMENT_DISPLAY_TYPES.has(item.displayType) ? (
                    <MediaPreviewDialog
                      details={(
                        <span className="flex flex-wrap items-center gap-x-2">
                          <span>{regelLabel}</span>
                          <span aria-hidden="true">{SCHEIDING}</span>
                          <span>{t(`media.library.types.${item.displayType}`)}</span>
                          {datum != null && !Number.isNaN(datum.getTime()) && (
                            <>
                              <span aria-hidden="true">{SCHEIDING}</span>
                              <span>{formatDate(datum)}</span>
                            </>
                          )}
                        </span>
                      )}
                      labels={{
                        title: item.fileName,
                        imageAlt: t('media.library.card.thumbnailAlt', { fileName: item.fileName }),
                        placeholder: t('media.gallery.placeholder'),
                        loading: t('media.library.card.downloading'),
                        close: t('common.close'),
                        download: t('media.library.card.download'),
                      }}
                      loadImageSrc={() => loadWebImage(item)}
                      onDownload={() => onDownload(item)}
                      trigger={(
                        <button
                          aria-label={t('media.gallery.enlarge', { fileName: item.fileName })}
                          className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
                          type="button"
                        >
                          {item.thumbnailUrl != null ? (
                            <img alt="" className="size-10 rounded object-cover" src={item.thumbnailUrl} />
                          ) : (
                            <span aria-hidden="true" className="material-symbols-outlined text-[20px] text-[var(--foreground-muted)]">{IMAGE_ICON}</span>
                          )}
                        </button>
                      )}
                    />
                  ) : item.thumbnailUrl != null ? (
                    <img alt="" className="size-10 rounded object-cover" src={item.thumbnailUrl} />
                  ) : (
                    <span aria-hidden="true" className="material-symbols-outlined text-[20px] text-[var(--foreground-muted)]">{IMAGE_ICON}</span>
                  )}
                </td>
                <td className="max-w-[280px] truncate p-2 font-medium text-[var(--foreground)]">{item.fileName}</td>
                <td className="p-2"><Badge variant="secondary">{t(`media.library.types.${item.displayType}`)}</Badge></td>
                <td className="max-w-[260px] truncate p-2 text-[var(--foreground-muted)]">
                  {item.productId == null ? (
                    t('media.library.card.unlinked')
                  ) : href != null ? (
                    <Link className="text-[var(--secondary)] underline-offset-2 hover:underline" to={href}>
                      {t('media.library.card.productLine', { brand: item.brand, productName: item.productName, gtin: item.gtin })}
                    </Link>
                  ) : (
                    t('media.library.card.productLine', { brand: item.brand, productName: item.productName, gtin: item.gtin })
                  )}
                </td>
                <td className="whitespace-nowrap p-2 text-[var(--foreground-muted)]">
                  {datum == null || Number.isNaN(datum.getTime()) ? LEEG : formatDate(datum)}
                </td>
                <td className="p-2">
                  <Button disabled={busy} onClick={() => onDownload(item)} size="sm" variant="ghost">
                    {busy ? (
                      <>
                        <span aria-hidden="true" className="material-symbols-outlined animate-spin text-[16px]" data-testid={`media-row-downloading-${item.id}`}>{SPINNER_ICON}</span>
                        {t('media.library.card.downloading')}
                      </>
                    ) : (
                      <>
                        <span aria-hidden="true" className="material-symbols-outlined text-[16px]">{DOWNLOAD_ICON}</span>
                        {t('media.library.card.download')}
                      </>
                    )}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
